import { useQueryClient } from '@tanstack/react-query';
import useMutationApi from '../useMutationApi';
import { DeltaAnalysisResult } from '@erp/shared-types';

// Types matching backend
export interface PendingChange {
  type: 'add_rule' | 'edit_rule' | 'delete_rule';
  entityId?: string;
  data?: Record<string, unknown>;
}

export interface ApplyChangesResponse {
  success: boolean;
  orphanedInvoices: number;
}

/**
 * Hook to analyze the delta/impact of pending rule changes
 */
export const useAnalyzeDelta = (agreementId: string) => {
  return useMutationApi<
    { pendingChanges: PendingChange[] },
    DeltaAnalysisResult
  >({
    method: 'POST',
    endpoint: `rebates/agreements/${agreementId}/analyze-delta`,
  });
};

/**
 * Hook to apply pending rule changes to an agreement
 */
export const useApplyChanges = (agreementId: string) => {
  const queryClient = useQueryClient();

  return useMutationApi<
    { pendingChanges: PendingChange[]; acknowledged?: boolean },
    ApplyChangesResponse
  >({
    method: 'POST',
    endpoint: `rebates/agreements/${agreementId}/apply-changes`,
    options: {
      onSuccess: () => {
        // Invalidate agreement data to refetch updated rules
        queryClient.invalidateQueries({
          queryKey: ['rebateAgreement', agreementId],
        });
        queryClient.invalidateQueries({
          queryKey: ['rebateAgreements'],
        });

        // Invalidate rebate invoices (may be deleted during destructive changes)
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/invoices'),
        });

        // Invalidate manual amounts (may be affected by rule deletions)
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/manual-amounts'),
        });

        // Invalidate payment allocations (may be affected by rule deletions)
        queryClient.invalidateQueries({
          queryKey: ['paymentAllocations'],
        });

        // Invalidate supplier payments (allocations may change)
        queryClient.invalidateQueries({
          queryKey: ['supplierPayments'],
        });
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/supplier-payments'),
        });

        // Invalidate product groups (active set may change when rules are deleted)
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/product-groups'),
        });

        // Invalidate line items (volumes depend on rule product groups/divisions)
        // When rules change, volumes need to be recalculated
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/line-items'),
        });

        // Invalidate forecast data (tier calculations and rebate totals depend on rule configuration)
        // When rules change (tiers, product groups, divisions), forecast needs recalculation
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/forecast'),
        });
      },
    },
  });
};
